import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  Play, Pause, Volume2, VolumeX, Calendar,
  MapPin, Users, ChevronDown, Sparkles, Heart
} from 'lucide-react';

// Import your local video file
import localVideo from '../assets/videos/7418472-uhd_3840_2160_25fps.mp4';

const LandingPage = () => {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();               // 👈 added for scroll state

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [visibleBoxes, setVisibleBoxes] = useState([false, false, false]);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // ---------- SCROLL TO EVENT SECTION WHEN BOOK EVENT IS CLICKED ----------
  useEffect(() => {
    if (location.state?.scrollToEvent) {
      const element = document.getElementById('event-specialties');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // Clear the state so it doesn't scroll again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // ---------- VIDEO CONTROLS ----------
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => setIsPlaying(false));
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // ---------- INITIALISE VIDEO ----------
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.autoplay = true;
      videoRef.current.loop = true;
      videoRef.current.muted = true;
      videoRef.current.playsInline = true;
      videoRef.current.preload = "auto";

      const handleLoaded = () => {
        setIsVideoLoaded(true);
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
        }
      };

      const handleError = () => {
        setIsVideoLoaded(false);
        setIsPlaying(false);
      };

      videoRef.current.addEventListener('loadeddata', handleLoaded);
      videoRef.current.addEventListener('error', handleError);
      videoRef.current.addEventListener('canplay', handleLoaded);

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadeddata', handleLoaded);
          videoRef.current.removeEventListener('error', handleError);
          videoRef.current.removeEventListener('canplay', handleLoaded);
        }
      };
    }
  }, []);

  // ---------- SCROLL ANIMATION FOR EVENT BOXES ----------
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;

      if (scrollPosition > windowHeight * 0.3) {
        setVisibleBoxes([true, false, false]);
      }
      if (scrollPosition > windowHeight * 0.5) {
        setVisibleBoxes([true, true, false]);
      }
      if (scrollPosition > windowHeight * 0.7) {
        setVisibleBoxes([true, true, true]);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ---------- CLICK HANDLER FOR EVENT CARDS ----------
  const handleEventBoxClick = (eventType) => {
    switch (eventType) {
      case 'wedding':
        navigate('/book-wedding');
        break;
      case 'birthday':
        navigate('/book-birthday');
        break;
      case 'trip':
        navigate('/book-trip');
        break;
      default:
        break;
    }
  };

  const eventTypes = [
    {
      id: 1,
      type: 'wedding',
      title: 'Wedding Celebrations',
      description: 'Create magical moments that last forever with our exquisite wedding planning',
      image: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=800',
      features: ['Venue Selection', 'Catering', 'Decoration', 'Entertainment'],
      color: 'from-pink-400 to-rose-400',
      icon: '💒',
    },
    {
      id: 2,
      type: 'birthday',
      title: 'Birthday Parties',
      description: 'Make every birthday unforgettable with our fun and creative party planning',
      image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=800',
      features: ['Theme Planning', 'Games & Activities', 'Cake & Food', 'Party Favors'],
      color: 'from-yellow-400 to-orange-400',
      icon: '🎂',
    },
    {
      id: 3,
      type: 'trip',
      title: 'Corporate Trips',
      description: 'Plan memorable corporate retreats and team-building adventures',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800',
      features: ['Transportation', 'Accommodation', 'Activities', 'Team Building'],
      color: 'from-blue-400 to-cyan-400',
      icon: '✈️',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-pink-50">
      <Navbar />

      {/* ===== HERO SECTION – VIDEO WITH APPEALING TEXT OVERLAY ===== */}
      <section className="relative h-screen overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 bg-black">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={localVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Subtle dark gradient overlay to make text pop */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>

          {/* Loading overlay */}
          {!isVideoLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-100 to-pink-100 flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mb-4"></div>
                <p className="text-gray-600">Loading joyful moments...</p>
              </div>
            </div>
          )}

          {/* Play button overlay if video is not playing */}
          {!isPlaying && isVideoLoaded && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <button
                onClick={togglePlay}
                className="p-6 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-300 hover:scale-110 shadow-2xl transform hover:-translate-y-1"
                title="Play Video"
              >
                <Play size={48} className="text-gray-800" />
              </button>
            </div>
          )}

          {/* ===== HERO TEXT – LIVELY, JOYFUL, VISUALLY APPEALING ===== */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
            <div className="inline-flex items-center justify-center p-3 bg-white/20 backdrop-blur-md rounded-full mb-6">
              <Sparkles className="h-8 w-8 text-yellow-300 mr-2" />
              <Heart className="h-8 w-8 text-pink-300" />
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 drop-shadow-lg animate-fadeInUp">
              Celebrate Life’s
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent block md:inline">
                {" "}Beautiful Moments
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-2xl mb-8 drop-shadow-md animate-fadeInUp animation-delay-200">
              From intimate gatherings to grand celebrations – we turn your dreams into unforgettable memories.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('event-specialties');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-pink-400 text-white text-lg font-semibold rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 animate-fadeInUp animation-delay-400"
            >
              Explore Our Services
            </button>
          </div>

          {/* Video Controls */}
          <div className="absolute bottom-8 right-8 flex space-x-4 z-20">
            <button
              onClick={togglePlay}
              className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-300 hover:scale-110 shadow-lg"
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause size={24} className="text-gray-800" /> : <Play size={24} className="text-gray-800" />}
            </button>
            <button
              onClick={toggleMute}
              className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-300 hover:scale-110 shadow-lg"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <VolumeX size={24} className="text-gray-800" /> : <Volume2 size={24} className="text-gray-800" />}
            </button>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <ChevronDown className="h-8 w-8 text-white animate-bounce" />
          </div>
        </div>
      </section>

      {/* ===== EVENT SPECIALTIES SECTION – WITH ID FOR SMOOTH SCROLL ===== */}
      <section id="event-specialties" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
              Our Event Specialties
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Click on any event type to start planning your celebration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {eventTypes.map((event, index) => (
              <div
                key={event.id}
                className={`transform transition-all duration-1000 ${
                  visibleBoxes[index] ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div
                  className="group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                  onClick={() => handleEventBoxClick(event.type)}
                  onMouseEnter={(e) => (e.currentTarget.style.cursor = 'pointer')}
                >
                  {/* Image Container */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${event.color} opacity-20`}></div>
                    <div className="absolute top-4 right-4 text-4xl bg-white/90 backdrop-blur-sm p-3 rounded-xl">
                      {event.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-yellow-600 transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 mb-6">{event.description}</p>

                    {/* Features */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-yellow-500" />
                        What we handle:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {event.features.map((feature, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-gray-600">
                        <div className="flex items-center">
                          <Users className="h-5 w-5 mr-1 text-pink-500" />
                          <span className="text-sm">20-500+ Guests</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="h-5 w-5 mr-1 text-blue-500" />
                          <span className="text-sm">Any Venue</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${event.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;